import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { ContainerJourneyReleaseResponseDto, NumericReleaseResponseDto } from '@planning/app/api'
import { PaginatedList } from '@planning/components/PaginatedList'
import { CreateOrderModal } from '@planning/pages/Order/CreateOrderModal'
import numericReleaseService from '@planning/services/numericReleaseService'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { ContainerJourneyCard } from '@tom-ui/utils'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo } from 'react'
import { NumericReleaseContainerAssignmentPaginationStore } from '../../../Stores/NumericReleaseContainerAssignmentPaginationStore'

interface Props {
  numericRelease: NumericReleaseResponseDto
  isOpen: boolean
  onClose: () => void
}

const DialogWithDividers = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
}))

export const NumericReleaseAssignOrderDialog: FC<Props> = observer(props => {
  const { numericRelease, isOpen, onClose } = props
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const { appViewStore, dialogStore, isoCodeMappingInputStore, orderItemStore } = usePlanningStore()

  const numericReleaseContainerAssignmentPaginationStore = useMemo(
    () => new NumericReleaseContainerAssignmentPaginationStore(numericRelease.id),
    [numericRelease.id],
  )
  useEffect(() => {
    if (isOpen) {
      numericReleaseContainerAssignmentPaginationStore.loadCurrentPage()
    } else {
      // Timeout to allow dialog close animation to finish before resetting store
      setTimeout(() => numericReleaseContainerAssignmentPaginationStore.setFilter(), 300)
    }
  }, [numericReleaseContainerAssignmentPaginationStore, isOpen])

  const noContainersAvailable = () => {
    return (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        p={theme.customSpacing.xl}
      >
        <Typography>
          {t(
            'looksLikeThereAreNoContainersThatFitYourRequirements',
            'Looks like there are no containers that fit your requirements',
          )}
        </Typography>
      </Box>
    )
  }

  const handleAssign = async (containerJourney: ContainerJourneyReleaseResponseDto) => {
    if (!containerJourney.container.id) return

    try {
      await numericReleaseService.assignContainer({
        numericReleaseId: numericRelease.id,
        containerJourneyId: containerJourney.id,
      })

      appViewStore.setShowAlert('success', 'Container added')
      onClose()
    } catch {
      appViewStore.setShowAlert('error', 'Failed to add Container')
    }
  }

  const handleCreateOrder = () => {
    dialogStore.openDialog(
      <CreateOrderModal
        onSubmit={async createdOrders => {
          if (!createdOrders) return
          const outboundId = createdOrders['Outbound']

          if (outboundId) {
            try {
              const data = _.values(orderItemStore.elements).find(o => o.id === outboundId)

              if (!data?.containerJourney?.id) {
                console.error(
                  'Order data or ContainerJourneyID not found locally for ID:',
                  outboundId,
                )
                return
              }

              await numericReleaseService.assignContainer({
                numericReleaseId: numericRelease.id,
                containerJourneyId: data.containerJourney.id,
              })
            } catch (error) {
              console.error('Error calling assignContainer service:', error)
              appViewStore.setShowAlert('error', 'Failed to assign container')
            }
          }
          onClose()
          dialogStore.close()
        }}
        onCancel={() => {
          onClose()
          dialogStore.close()
        }}
        isoCodeMappingFieldRenderFn={isoCodeMappingInputStore.renderIsoCodeMappingField}
        outboundCarrierType={numericRelease.modeOfTransport}
        outboundCarrierVisitId={numericRelease.carrierVisitId ?? undefined}
        inheritedOperatorShortName={numericRelease.shippingLineShortName ?? undefined}
      />,
      true,
    )
  }

  if (!numericRelease) {
    return null
  }

  const hasContainersToAssign =
    numericReleaseContainerAssignmentPaginationStore.totalCount > 0 ||
    !!numericReleaseContainerAssignmentPaginationStore.filter

  return (
    <DialogWithDividers open={isOpen} onClose={onClose} maxWidth='md' fullWidth scroll='body'>
      <DialogTitle>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          {t('assignContainer', 'Assign container')}

          <Stack direction='row' alignItems='center' spacing={1}>
            <Button variant='contained' color='inherit' onClick={handleCreateOrder}>
              {t('createOrder', 'Create order')}
            </Button>

            <IconButton
              aria-label='close'
              onClick={onClose}
              sx={{
                color: theme => theme.palette.text.primary,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack>
          {!hasContainersToAssign ? (
            noContainersAvailable()
          ) : (
            <PaginatedList
              paginatedStore={numericReleaseContainerAssignmentPaginationStore}
              hidePaginationControl
              filterable
              searchPlaceholderText={t('searchByContainerNumber', 'Search by container number')}
              noPadding
            >
              <Stack direction='column' spacing={theme.customSpacing.xs}>
                {numericReleaseContainerAssignmentPaginationStore.pageItems
                  .filter(cj => cj.container)
                  .map(containerJourney => {
                    return (
                      <ContainerJourneyCard
                        key={`assign-container-journey-item-${containerJourney.id}`}
                        container={containerJourney.container!}
                        inbound={containerJourney.inboundOrder}
                        outbound={containerJourney.outboundOrder}
                        secondaryActionLabel={t('assign', 'Assign')}
                        onSecondaryAction={() => handleAssign(containerJourney)}
                      />
                    )
                  })}
              </Stack>
            </PaginatedList>
          )}
        </Stack>
      </DialogContent>
    </DialogWithDividers>
  )
})
